import { Button } from "@/atoms";
import { Select } from "@/molecules";
import { IconGridSelect } from "@/molecules/selects/IconGridSelect";
import {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ChevronDown, X } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { ButtonProps } from "@oneleet/components/src/atoms/buttons/Button/Button";
import {
  MultiSelectProps as ReusableMultiSelectProps,
  SingleSelectProps as ReusableSingleSelectProps,
  SelectOption,
  Props as SelectProps,
} from "@oneleet/components/src/molecules/selects/Select/Select";

const MIN_SELECT_POPOVER_WITH_SEARCH_WIDTH = 242;

interface BaseProps<T> {
  id?: string;
  placeholder?: string;
  isDisabled?: boolean;
  limitDropdownWidthToBox?: boolean;
  highlightWhenOptionIsSelected?: boolean;
  className?: string;
  classNames?: {
    button?: ButtonProps["classNames"];
    select?: SelectProps<T>["classNames"];
  };
}

interface SingleSelectProps<T>
  extends ReusableSingleSelectProps<T>,
    BaseProps<T> {
  multiselect?: false | undefined;
  grid?: number;
  hideSelectedLabel?: boolean;
  startIcon?: ({
    selectedOption,
  }: {
    selectedOption: SelectOption<T> | null;
  }) =>
    | React.ReactElement<{
        className?: string;
      }>
    | undefined;
  startDecorator?: ({
    selectedOption,
  }: {
    selectedOption: SelectOption<T> | null;
  }) =>
    | React.ReactElement<{
        className?: string;
      }>
    | undefined;
}

interface MultiSelectProps<T>
  extends ReusableMultiSelectProps<T>,
    BaseProps<T> {
  multiselect: true;
  grid?: number;
  hideSelectedLabel?: boolean;
  startIcon?: ({ selectedOptions }: { selectedOptions: SelectOption<T>[] }) =>
    | React.ReactElement<{
        className?: string;
      }>
    | undefined;
  startDecorator?: ({
    selectedOptions,
  }: {
    selectedOptions: SelectOption<T>[];
  }) =>
    | React.ReactElement<{
        className?: string;
      }>
    | undefined;
}

export type SelectboxProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

/**
 * A button that supports selecting and displaying one or more values from a select
 */
function Selectbox<T>({
  value,
  defaultValue,
  multiselect = false,
  grid = 0,
  hideSelectedLabel = false,
  enableClearingSelection = true,
  placeholder = "Select an option",
  startIcon,
  startDecorator,
  enableSearch = true,
  isDisabled = false,
  limitDropdownWidthToBox = true,
  highlightWhenOptionIsSelected = false,
  id,
  className,
  classNames,
  onSelect,
  ...rest
}: SelectboxProps<T>) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);

  let initialSelectedOption: SelectOption<T> | null = null;

  if (value === undefined && defaultValue !== undefined) {
    initialSelectedOption = Array.isArray(defaultValue) ? null : defaultValue;
  }

  let initialSelectedOptions: SelectOption<T>[] = [];
  if (value === undefined && defaultValue !== undefined) {
    initialSelectedOptions = Array.isArray(defaultValue) ? defaultValue : [];
  }
  // selectedOption and selectedOptions state are not used if value is provided (selectbox is controlled)
  const [selectedOption, setSelectedOption] = useState<SelectOption<T> | null>(
    initialSelectedOption,
  );
  const [selectedOptions, setSelectedOptions] = useState<SelectOption<T>[]>(
    initialSelectedOptions,
  );

  const updateWidth = useCallback(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);

    if (buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateWidth]);

  let buttonLabel = placeholder;
  if (value !== undefined) {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        buttonLabel = `${value[0].label}`;
        if (value.length > 1) {
          buttonLabel += ` + ${value.length - 1} more`;
        }
      }
    } else if (value !== null) {
      buttonLabel = value.label;
    }
  } else if (selectedOption !== null) {
    buttonLabel = selectedOption.label;
  } else if (selectedOptions.length > 0) {
    buttonLabel = selectedOptions.map((option) => option.label).join(", ");
  }

  const itemIsSelected = useMemo(() => {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else {
        return value !== null;
      }
    } else {
      return selectedOption !== null || selectedOptions.length > 0;
    }
  }, [value, selectedOption, selectedOptions]);

  const computedStartIcon = useMemo(() => {
    const result = multiselect
      ? (
          startIcon as ({
            selectedOptions,
          }: {
            selectedOptions: SelectOption<T>[];
          }) => React.ReactElement<{
            className?: string;
          }>
        )?.({
          selectedOptions:
            value !== undefined
              ? (value as SelectOption<T>[])
              : selectedOptions,
        })
      : (
          startIcon as ({
            selectedOption,
          }: {
            selectedOption: SelectOption<T> | null;
          }) => React.ReactElement<{
            className?: string;
          }>
        )?.({
          selectedOption:
            value !== undefined
              ? (value as SelectOption<T> | null)
              : selectedOption,
        });

    return result
      ? cloneElement(result, {
          className: twMerge(
            "size-5 shrink-0",
            itemIsSelected && highlightWhenOptionIsSelected
              ? "text-brand-500"
              : "text-gray-500",
            result.props.className,
          ),
        })
      : undefined;
  }, [
    value,
    selectedOption,
    selectedOptions,
    startIcon,
    multiselect,
    itemIsSelected,
    highlightWhenOptionIsSelected,
  ]);
  const computedStartDecorator = useMemo(() => {
    const result = multiselect
      ? (
          startDecorator as ({
            selectedOptions,
          }: {
            selectedOptions: SelectOption<T>[];
          }) => React.ReactElement<{
            className?: string;
          }>
        )?.({
          selectedOptions:
            value !== undefined
              ? (value as SelectOption<T>[])
              : selectedOptions,
        })
      : (
          startDecorator as ({
            selectedOption,
          }: {
            selectedOption: SelectOption<T> | null;
          }) => React.ReactElement<{
            className?: string;
          }>
        )?.({
          selectedOption:
            value !== undefined
              ? (value as SelectOption<T> | null)
              : selectedOption,
        });

    // if value is provided (selectbox is controlled), selectedOption and selectedOptions state are not used
    if (
      !result &&
      value !== undefined &&
      (value as SelectOption<T>)?.startDecorator
    ) {
      return (value as SelectOption<T>).startDecorator;
    }

    return result ? result : undefined;
  }, [value, selectedOption, selectedOptions, startDecorator, multiselect]);

  const renderSelectChildren = () => {
    return (
      <Button
        id={id}
        label={hideSelectedLabel ? "" : buttonLabel}
        startIcon={computedStartIcon}
        startDecorator={computedStartDecorator}
        variant={
          itemIsSelected && highlightWhenOptionIsSelected ? "soft" : "outlined"
        }
        color={
          itemIsSelected && highlightWhenOptionIsSelected ? "brand" : "gray"
        }
        size="large"
        disabled={isDisabled}
        className={twMerge(
          "focus:outline-brand-400 justify-between px-3 text-left font-normal hover:enabled:bg-transparent",
          (Array.isArray(value) ? value.length > 0 : value) ||
            selectedOption ||
            selectedOptions.length > 0
            ? "text-gray-900"
            : "text-gray-500",
          itemIsSelected &&
            highlightWhenOptionIsSelected &&
            "text-brand-500 ring-brand-400 ring-1 ring-inset",
          className,
        )}
        classNames={{
          ...classNames?.button,
          label: twMerge("line-clamp-1 text-left", classNames?.button?.label),
        }}
        endIcon={
          enableClearingSelection &&
          ((Array.isArray(value) && value.length > 0) ||
            (!Array.isArray(value) && value) ||
            selectedOption !== null ||
            selectedOptions.length > 0) ? (
            <div
              role="button"
              className={twMerge(
                "flex items-center justify-center rounded-md bg-transparent text-gray-500 hover:text-gray-600",
                itemIsSelected &&
                  highlightWhenOptionIsSelected &&
                  "text-brand-500 hover:text-brand-600",
              )}
            >
              <X
                className="size-5 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  if (value === undefined) {
                    setSelectedOption(null);
                    setSelectedOptions([]);
                  }
                  if (Array.isArray(value) || selectedOptions.length > 0) {
                    (onSelect as (option: SelectOption<T>[]) => void)?.([]);
                  } else {
                    (onSelect as (option: SelectOption<T> | null) => void)?.(
                      null,
                    );
                  }
                }}
              />
            </div>
          ) : (
            <ChevronDown className="text-gray-500" />
          )
        }
        ref={buttonRef}
      />
    );
  };

  const minWidth = buttonWidth
    ? Math.max(
        buttonWidth,
        enableSearch ? MIN_SELECT_POPOVER_WITH_SEARCH_WIDTH : 0,
      )
    : enableSearch
      ? MIN_SELECT_POPOVER_WITH_SEARCH_WIDTH
      : 0;
  const maxWidth = buttonWidth
    ? Math.max(buttonWidth, MIN_SELECT_POPOVER_WITH_SEARCH_WIDTH)
    : enableSearch
      ? MIN_SELECT_POPOVER_WITH_SEARCH_WIDTH
      : minWidth;
  const minWidthString = `${minWidth}px`;
  const maxWidthString = `${maxWidth}px`;

  return (
    <>
      {multiselect ? (
        <Select
          value={
            value !== undefined && Array.isArray(value)
              ? value
              : selectedOptions
          }
          defaultValue={
            defaultValue !== undefined && Array.isArray(defaultValue)
              ? defaultValue
              : undefined
          }
          multiselect
          enableSearch={enableSearch}
          classNames={classNames?.select}
          style={{
            minWidth: minWidthString,
            ...(limitDropdownWidthToBox ? { maxWidth: maxWidthString } : {}),
          }}
          onSelect={(newValue) => {
            if (value === undefined) {
              setSelectedOptions(newValue);
            }

            (onSelect as (option: SelectOption<T>[]) => void)?.(newValue);
          }}
          {...rest}
        >
          {renderSelectChildren()}
        </Select>
      ) : grid > 0 ? (
        <IconGridSelect
          value={
            value !== undefined && !Array.isArray(value)
              ? value
              : selectedOption
          }
          defaultValue={
            defaultValue !== undefined && !Array.isArray(defaultValue)
              ? defaultValue
              : undefined
          }
          itemsPerRow={grid}
          enableSearch={enableSearch}
          enableClearingSelection={enableClearingSelection}
          classNames={classNames?.select}
          style={{
            minWidth: minWidthString,
            ...(limitDropdownWidthToBox ? { maxWidth: maxWidthString } : {}),
          }}
          onSelect={(newValue) => {
            if (value === undefined) {
              setSelectedOption(newValue);
            }

            (onSelect as (option: SelectOption<T> | null) => void)?.(newValue);
          }}
          {...rest}
        >
          {renderSelectChildren()}
        </IconGridSelect>
      ) : (
        <Select
          value={
            value !== undefined && !Array.isArray(value)
              ? value
              : selectedOption
          }
          defaultValue={
            defaultValue !== undefined && !Array.isArray(defaultValue)
              ? defaultValue
              : undefined
          }
          enableSearch={enableSearch}
          enableClearingSelection={enableClearingSelection}
          classNames={classNames?.select}
          style={{
            minWidth: minWidthString,
            ...(limitDropdownWidthToBox ? { maxWidth: maxWidthString } : {}),
          }}
          onSelect={(newValue) => {
            if (value === undefined) {
              setSelectedOption(newValue);
            }

            (onSelect as (option: SelectOption<T> | null) => void)?.(newValue);
          }}
          {...rest}
        >
          {renderSelectChildren()}
        </Select>
      )}
    </>
  );
}

export default Selectbox;
